import { AxiosResponse } from "axios";
import { Action } from "redux-saga";
import { call, CallEffect, PutEffect } from "redux-saga/effects";
import createAccount from "../account/create";
import { clearString, getFirstAndLastName, getFormattedCart, getFormattedDeviceFingerPrintID } from "../../../utils/functions";
import api from "../../../settings/api";
import apiPCI from "../../../settings/apiPCI";


export interface Fetch {
    plate: string;
    card: {
        number: string;
        name: string;
        expiration: string;
        document: string;
    }
    address: {
        zipcode: string;
        streetName: string;
        neighborhood: string;
        addressStreet: string;
        number: string;
        complement: string;
        city: string;
        state: string;
        stateCode: string;
    };
    visitorId: string;
    callback: (success: boolean, errorMessage?: string) => void;
}

export interface PaymentCardAction {
    type: string;
    payload: Fetch;
}

export default function* paymentCard({
    payload,
}: PaymentCardAction): Generator<
    CallEffect<unknown> | PutEffect<Action>,
    void,
    AxiosResponse
> {
    try {

        const deviceFingerprintID = getFormattedDeviceFingerPrintID(payload.visitorId);

        const account: unknown = yield call(createAccount, {
            type: "",
            payload: {
                document: payload?.card?.document,
                name: payload?.card?.name,
            }
        });

        if (!account) throw new Error();

        const uriPCI = "/api/v1/tokenizing";
        const responsePCI = yield call(apiPCI.post, uriPCI, {
            dataToToken: clearString(payload?.card?.number),
        });

        const uriBrands = "/v1/financeiro/bandeiras";
        const responseBrands = yield call(api.get, uriBrands);

        const {
            total,
            selected,
        } = getFormattedCart({
            // @ts-expect-error sagas types
            debts: payload?.debts,
            // @ts-expect-error sagas types
            cart: payload?.cart,
        });

        const brand = responseBrands?.data?.listaBandeiraCartao?.find((item: {
            descricaoBandeira: string;
        }) => item?.descricaoBandeira === responsePCI?.data?.additionalInformation?.brand?.name);

        const { firstName, lastName } = getFirstAndLastName(payload?.card?.name);

        const responseValidate = yield call(api.post, `/v1/payments/credit-card/validate`, {
            creditCardData: {
                buyerInformation: {
                    document: clearString(payload?.card?.document),
                    // @ts-expect-error sagas types
                    nrc: account?.customerId
                },
                deviceInformation: {
                    fingerprintSessionId: deviceFingerprintID,
                },
                orderInformation: {
                    billTo: {
                        country: "BR",
                        firstName: firstName,
                        lastName: lastName,
                    },
                    totalAmount: Number(Number(total)?.toFixed(2))
                },
                paymentInformation: {
                    card: {
                        cardIdentifier: brand?.codigoBandeira,
                        expirationMonth: payload?.card?.expiration?.split('/')?.[0],
                        expirationYear: payload?.card?.expiration?.split('/')?.[1],
                        number: responsePCI?.data?.token
                    }
                }
            },
        });

        const uriMethods = `/v1/payments/methods`;
        const responseMethods = yield call(api.get, uriMethods);

        const method = responseMethods?.data?.data?.find((item: {
            method: string
        }) => item.method === "credit-card");

        const body = {
            document: clearString(payload?.card?.document),
            // @ts-expect-error sagas types
            requestId: payload?.requestId,
            // @ts-expect-error sagas types
            customerIdentification: account?.customerId,
            // @ts-expect-error sagas types
            accountIdentification: account?.accountId,
            paymentValue: Number(Number(total)?.toFixed(2)),
            licensePlate: clearString(payload.plate.toLocaleUpperCase()),
            emissionChannel: method?.code,
            tollPassages: selected,
            creditCardNumber: responsePCI?.data?.token,
            creditCardOwner: payload?.card?.name,
            cardIdentifier: brand?.codigoBandeira,
            monthDueDate: Number(payload?.card?.expiration?.split('/')?.[0]),
            yearDueDate: Number(payload?.card?.expiration?.split('/')?.[1]),
            creditCardValidationToken: responseValidate?.data?.data?.validationToken,
            deviceInformation: {
                fingerprintSessionId: deviceFingerprintID
            }
        };

        const response = yield call(api.post, `/v1/payments/credit-card`, body);

        if (response?.data?.idTransaction) {
            yield call(payload.callback, true);
        } else {
            yield call(payload.callback, false, "error");
        }

    } catch (error) {
        yield call(payload.callback, false, "timeout");
    }
}